import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F8916LimService } from './f8916-lim.service';

import { safeEvalFormula, isFiniteEvalNumber } from '../../../shared/_utils/safe-eval-formula';

@Component({
    selector: 'gtw-f8916-lim',
    templateUrl: './f8916-lim.component.html',
    styleUrls: ['./f8916-lim.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F8916LimComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS (similar to F8916 pattern)
  rowData: any = {};

  // Component properties migrated from F8916LimCtrl.js
  modalTitle = 'Reconciliation of Schedule M-3 Taxable Income With Tax Return Taxable Income for Mixed Groups';
  originalData: any[] = [];
  originalOutterData: any[] = [];
  userSettings: any;
  logged_in_user: string = '';
  currentyear: string = '';
  leId: string = '';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  disableDate = true;
  isSaveClicked = false;
  crudLoading = false;
  minimise = false;
  showFormLoader = true;
  currentTabName = "Form Entry";
  
  // Form data structures
  formHeader: any[] = [];
  tableHeader: any[] = [];
  formList1: any[] = [];
  formList: any[] = [];
  outerList: any[] = [];
  formCalcData: any[] = [];
  
  // Entity information
  entityList: any[] = [];

  // Tab configuration (like F8916)
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;

  // Calculation flags
  form8936DataChanged1 = false;
  form8936DataChanged2 = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f8916LimService: F8916LimService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      // Only proceed if we have row data
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupComponentData();
        this.init8916();
      }
    } catch (e) {
      console.error('Error in F8916Lim init:', e);
    }
  }

  private setupComponentData() {
    // Use real data from rowData parameter (like in F8916 and F1125SchE pattern)
    this.currentyear = this.rowData.TAX_YEAR || '';
    this.leId = this.rowData.LEID || '';
    this.cdr_no = this.rowData.MARS || '';
    this.leType = this.rowData.LE_TYPE || '';
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR || ''},
      {key: "LEID", value: this.rowData.LEID || ''},
      {key: "LE Name", value: this.rowData.LE_NAME || ''}
    ];
  }

  init8916() {
    this.fetch8916records();
  }

  fetch8916records() {
    const params = { 
      "action_code": 'sl17nu', 
      "tax_year": this.rowData.TAX_YEAR, 
      "scenario": 32, 
      "jcd_key": this.rowData.JCD_KEY, 
      "combination_key": this.rowData.COMBINATION_KEY,
      "form_no": "8916", 
      "schedule_name": "Limitation", 
      "part_no": "NA", 
      "section_name": "NA", 
      "jcd_ta_key": null
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.originalData = _.cloneDeep(this.formList);
      this.originalOutterData = _.cloneDeep(this.outerList);
      this.fetchModalCalcData();
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    this.modalTitle = data.jsonObject[0].ATTRIB_DESCRIPTION;
    this.formHeader = data.jsonObject.filter((item: any) => item.ROW_TYPE === "FH");
    this.tableHeader = data.jsonObject.filter((item: any) => item.ROW_TYPE === "TH");
    this.formList1 = data.jsonObject.slice(1);

    this.formList = this.formList1.filter((obj: any) => obj.ROW_TYPE !== 'TH');

    // Build outerList for unique form lines
    this.outerList = [];
    for (let i = 0; i < this.formList.length; i++) {
      if (this.formList[i].FORM_LINE_KEY != null) {
        let tempCheck1: any[] = [];
        if (this.outerList != null && this.outerList.length > 0) {
          tempCheck1 = this.outerList.filter((item: any) => 
            item.FORM_LINE_KEY === this.formList[i].FORM_LINE_KEY && item.IS_FORM_DISPLAY === 'Y'
          );
        }
        if (tempCheck1 === undefined || tempCheck1.length === 0) {
          this.outerList.push(this.formList[i]);
        }
      }
    }

    // Set default values for specific lines (from original controller)
    for (let j = 0; j < this.formList.length; j++) {
      if (this.formList[j].ATTRIB_SHORT_CODE === "F8916_2c1_LINE_PRE_LIMITATION") {
        this.formList[j].ATTRIB_VALUE = 500;
      } else if (this.formList[j].ATTRIB_SHORT_CODE === "F8916_2c1_LINE_INPUT") {
        this.formList[j].ATTRIB_VALUE = -300;
      } else if (this.formList[j].ATTRIB_SHORT_CODE === "F8916_2d1_LINE_PRE_LIMITATION") {
        this.formList[j].ATTRIB_VALUE = -100;
      } else if (this.formList[j].ATTRIB_SHORT_CODE === "F8916_2e2_LINE_PRE_LIMITATION") {
        this.formList[j].ATTRIB_VALUE = 600;
      } else if (this.formList[j].ATTRIB_SHORT_CODE === "F8916_4a1_LINE_PRE_LIMITATION") {
        this.formList[j].ATTRIB_VALUE = -200;
      } else if (this.formList[j].ATTRIB_SHORT_CODE === "F8916_4b1_LINE_PRE_LIMITATION") {
        this.formList[j].ATTRIB_VALUE = -100;
      }
    }
  }

  fetchModalCalcData() {
    const params = { 
      "action_code": '1ubsuu', 
      "tax_year": this.rowData.TAX_YEAR, 
      "scenario": null, 
      "jcd_key": this.rowData.JCD_KEY, 
      "form_no": "8916",
      "schedule_name": "Limitation",
      "part_no": "NA",
      "section_name": "NA" 
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.formCalcData = data.jsonObject;
      this.validateSubForm(null);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  validateSubForm(line: any) {
    this.form8936DataChanged1 = false;
    this.form8936DataChanged2 = false;
    
    if (line == null || line.DATA_TYPE == 'number') {
      for (let g = 0; g < this.formCalcData.length; g++) {
        this.calcAmt(
          this.formCalcData[g].ATTRIB_CALC_FORMULA,
          this.formCalcData[g].LINE_NO,
          this.formCalcData[g].LINE_ATTRIB_KEY,
          this.formCalcData[g].ATTRIB_COL,
          this.formCalcData[g].EDIT_TYPE
        );
      }
    } else {
      line.IS_CHANGED_FLAG = 'Y';
    }
  }

  calcAmt(formula: string, line_no: string, attrib_key: string, attrib_col: string, edit_type: string) {
    let currentFormula = formula;


    let matches = formula.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[];

    if (matches == null) {
      lineFormulaArray = formula.split(/[+-/\\*\\]/);
    } else {
      const formulaLength = formula.length;
      const subFormula = formula.substring(formula.indexOf("("), formulaLength - 1);
      lineFormulaArray = subFormula.split(/[+-,/\\*\\]/);
    }

    for (let i = 0; i < lineFormulaArray.length; i++) {
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");

        const currentTabOldAtrribVal = this.originalData.filter((item: any) => item.FORM_NAME === "8916");
        const oldAttribVal = currentTabOldAtrribVal.filter((item: any) => 
          item.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
        );
        const currentTabNewAtrribVal = this.formList.filter((o: any) => 
          o.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
        );

        if (currentTabNewAtrribVal.length > 0) {
          const lineAmt = parseFloat(currentTabNewAtrribVal[0].ATTRIB_VALUE);

          if (oldAttribVal.length > 0 && lineAmt != parseFloat(oldAttribVal[0].ATTRIB_VALUE)) {
            currentTabNewAtrribVal[0].IS_CHANGED_FLAG = 'Y';
          }

          if (lineAmt >= 0) {
            currentFormula = currentFormula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
          } else {
            currentFormula = currentFormula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
          }
        }
      }
    }

    try {
      const calculatedVal = safeEvalFormula(currentFormula, null, 'f8916-lim');
      if (calculatedVal !== null && typeof calculatedVal !== 'undefined' && 
          isFiniteEvalNumber(calculatedVal)) {

        const changedFormulaAttribVal = this.formList.filter((item: any) => 
          item.LINE_NO === line_no && item.LINE_ATTRIB_KEY === attrib_key
        );

        if (changedFormulaAttribVal.length > 0) {
          if (parseFloat(String(changedFormulaAttribVal[0].ATTRIB_VALUE)) != parseFloat(calculatedVal.toFixed())) {
            if (currentFormula.substring(0, 4) === 'Math') {
              changedFormulaAttribVal[0].ATTRIB_VALUE = calculatedVal;
            } else {
              changedFormulaAttribVal[0].ATTRIB_VALUE = calculatedVal.toFixed();
            }

            changedFormulaAttribVal[0].IS_CHANGED_FLAG = 'Y';
          } else {
            if (currentFormula.substring(0, 4) === 'Math') {
              changedFormulaAttribVal[0].ATTRIB_VALUE = calculatedVal;
            } else {
              changedFormulaAttribVal[0].ATTRIB_VALUE = calculatedVal.toFixed();
            }
          }
        }
      }
    } catch (e) {
      console.error('Error in calculation:', e);
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build save data structure
    const saveData = this.formList.filter((item: any) => item.IS_CHANGED_FLAG === 'Y');
    
    if (saveData.length === 0) {
      this.gtwSnackbar.openSnackBar("No changes to save.", 'gtw-snackbar--yellow', true, 4);
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.f8916LimService.save8916Form(this.GENERAL_CONFIG.base_url, saveData, this.globalService, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          this.crudLoading = false;
          this.isSaveClicked = false;
          
          if (response.callSuccess === "1" && response.errorMessage === "null") {
            // Show success snackBar
            this.gtwSnackbar.openSnackBar('Transactions saved successfully', 'gtw-snackbar--green', true, 4);
            
            // Emit using @Output (AngularJS will close modal automatically)
            this.requestGridUpdate.emit('refresh');
          } else {
            const errorMessage = response.errorMessage !== 'null' ? response.errorMessage : 
              'Error occurred in saving the Transaction. If this continues please contact support';
            this.gtwSnackbar.openSnackBar(errorMessage, 'gtw-snackbar--red', true, 4);
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.crudLoading = false;
          this.isSaveClicked = false;
          this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
          this.cdr.markForCheck();
        }
      );
  }

  reset() {
    this.formList = _.cloneDeep(this.originalData);
    this.outerList = _.cloneDeep(this.originalOutterData);
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.LINE_ATTRIB_KEY || item.LEID || index : index;
  }
}
